import {Injectable} from '@angular/core';
import {
    AnalyticAgentReportType,
    ClientLegitimacyConfirmation,
    IncidentPriorityType,
    ReferenceBookApiService,
    ReferenceBookResponse,
    ReferenceBookType,
    RoleApiService,
    SubjectCategory,
    SubjectType,
} from '@npck/af-frontend-api';
import {LocalizationService} from '@npck/libs/commons';
import {isArray} from 'lodash-es';
import {combineLatest, filter, finalize, map, Observable, of, ReplaySubject, shareReplay} from 'rxjs';
import {AuthHolderService} from './auth-holder.service';

export const BIN_REFERENCES = ['bin', 'bintypes'];

const ROLE_REFERENCES = ['roles', 'composite_roles'];

@Injectable({
    providedIn: 'root',
})
export class DictionaryHolderService {
    commonDictionaries = [
        ReferenceBookType.COUNTRY,
        ReferenceBookType.CURRENCY,
        ReferenceBookType.FRAUD_TYPE,
        ReferenceBookType.TRANSACTION_STATUS,
        ReferenceBookType.TRANSACTION_TYPE,
        ReferenceBookType.INCIDENT_ROLE,
        ReferenceBookType.ACCOUNT_STATUS,
        ReferenceBookType.GENDER,
        ReferenceBookType.ATTACHED_DOC,
        ReferenceBookType.BIN,
    ];
    private references: Record<string, ReferenceBookResponse[]> = {};
    private pendingSubjects: Record<string, ReplaySubject<ReferenceBookResponse[]>> = {};
    private pendingTypes = new Set<string>();
    private inProgressRolesRequest: Observable<ReferenceBookResponse[]> | null = null;

    private subjectTypesDictionary: ReferenceBookResponse[] =
        this.buildDictionaryFromEnum(Object.assign(SubjectType), 'i18n_af_subjectType#');

    private subjectCategoryDictionary: ReferenceBookResponse[] =
        this.buildDictionaryFromEnum(Object.assign(SubjectCategory), 'i18n_af_category#');

    private incidentPriority: ReferenceBookResponse[] =
        this.buildDictionaryFromEnum(Object.assign(IncidentPriorityType), 'i18n_af_priorityLevel#');

    private subjectStatusDictionary: ReferenceBookResponse[] = [{
        code: 'true',
        name_ru: 'i18n_af_filter_field_status#deleted',
    }, {
        code: 'false',
        name_ru: 'i18n_af_filter_field_status#active',
    }];

    private notificationTypes = [
        {
            code: 'subject_categories_delete',
            code1: 'subject_categories_delete',
            code2: 'list',
        },
        {
            code: 'subject_categories_update',
            code1: 'subject_categories_update',
            code2: 'list',
        },
        {
            code: 'subject_create',
            code1: 'subject_create',
            code2: 'list',
        },
        {
            code: 'subject_delete',
            code1: 'subject_delete',
            code2: 'list',
        },
        {
            code: 'subject_update',
            code1: 'subject_update',
            code2: 'list',
        },
        {
            code: 'fraudster_delete',
            code1: 'fraudster_delete',
            code2: 'list',
        },
        {
            code: 'fraudster_update',
            code1: 'fraudster_update',
            code2: 'list',
        },
        {
            code: 'fraudster_create',
            code1: 'fraudster_create',
            code2: 'list',
        },
        {
            code: 'incident_create',
            code1: 'incident_create',
            code2: 'incident',
        },
        {
            code: 'incident_delete',
            code1: 'incident_delete',
            code2: 'incident',
        },
        {
            code: 'incident_update',
            code1: 'incident_update',
            code2: 'incident',
        },
        {
            code: 'incident_inquiry',
            code1: 'incident_inquiry',
            code2: 'incident',
        },
        {
            code: 'incident_inquiry_response',
            code1: 'incident_inquiry_response',
            code2: 'incident',
        },
        {
            code: 'incident_inquiry_create',
            code1: 'incident_inquiry_create',
            code2: 'incident',
        },
        {
            code: 'incident_inquiry_response_create',
            code1: 'incident_inquiry_response_create',
            code2: 'incident',
        },
        {
            code: 'incident_related_organizations_add',
            code1: 'incident_related_organizations_add',
            code2: 'incident',
        },
        {
            code: 'incident_related_organizations_delete',
            code1: 'incident_related_organizations_delete',
            code2: 'incident',
        },
        {
            code: 'refund_created',
            code1: 'refund_created',
            code2: 'refund',
        },
        {
            code: 'refund_update',
            code1: 'refund_update',
            code2: 'refund',
        },
        {
            code: 'refund_updated',
            code1: 'refund_updated',
            code2: 'refund',
        },
        {
            code: 'refund_deleted',
            code1: 'refund_deleted',
            code2: 'refund',
        },
    ];

    private clientConfirmedDictionary: ReferenceBookResponse[] = [{
        code: 'true',
        name_ru: 'i18n_af_clientConfirmed#true',
    }, {
        code: 'false',
        name_ru: 'i18n_af_clientConfirmed#false',
    }];

    private analyticAgentReportTypeDictionary: ReferenceBookResponse[] =
        this.buildDictionaryFromEnum(Object.assign(AnalyticAgentReportType), 'i18n_af_analyticAgentReportType#');

    private clientLegitimacyConfirmationDictionary: ReferenceBookResponse[] =
        this.buildDictionaryFromEnum(Object.assign(ClientLegitimacyConfirmation), 'i18n_af_clientLegitimacyConfirmation#');

    constructor(
        private referenceApi: ReferenceBookApiService,
        private authHolderService: AuthHolderService,
        private roleApiService: RoleApiService,
        private localizationService: LocalizationService,
    ) {
        this.authHolderService.isAuthorized
            .pipe(filter(isAuthorized => !isAuthorized))
            .subscribe(() => this.clearReferences(ROLE_REFERENCES));
    }

    getCustomizedReference(reference: ReferenceBookType): Observable<ReferenceBookResponse[]> {
        return this.getReference(reference)
            .pipe(map((response: ReferenceBookResponse[]) => {
                const undefinedItem = <ReferenceBookResponse>{
                    type: reference,
                    code: 'UNDEFINED',
                    code1: reference === ReferenceBookType.CURRENCY
                        ? this.localizationService.getLocalizedKey(`i18n_af_dictionary#${reference}#UNDEFINED`)
                        : 'UNDEFINED',
                    code2: 'UNDEFINED',
                    name_ru: this.localizationService.getLocalizedKey(`i18n_af_dictionary#${reference}#UNDEFINED`),
                };
                return [...response, undefinedItem];
            }));
    }

    clearReferences(references: string[]): void {
        references.forEach(reference => delete this.references[reference.toLowerCase()]);
    }

    getReference(reference: string): Observable<ReferenceBookResponse[]> {
        reference = reference.toLowerCase();

        if (reference in this.references) {
            return of(this.references[reference]);
        }
        if (reference === 'custom_notification_group_type') {
            return this.loadCustomizedNotificationType();
        }
        if (reference === 'custom_notification_type') {
            return of(this.notificationTypes);
        }
        if (reference === 'subject_type') {
            return of(this.subjectTypesDictionary);
        }
        if (reference === 'incident_priority') {
            return of(this.incidentPriority);
        }
        if (reference === 'subject_status') {
            return of(this.subjectStatusDictionary);
        }
        if (reference === 'subject_category') {
            return of(this.subjectCategoryDictionary);
        }
        if (ROLE_REFERENCES.includes(reference)) {
            return this.loadRoles(reference);
        }
        if (reference === 'bintypes') {
            return this.loadBinTypes();
        }
        if (reference === 'client_confirmed') {
            return of(this.clientConfirmedDictionary);
        }
        if (reference === 'client_legitimacy_confirmation') {
            return of(this.clientLegitimacyConfirmationDictionary);
        }
        if (reference === 'analytic_agent_report_type') {
            return of(this.analyticAgentReportTypeDictionary);
        }

        const existing = this.pendingSubjects[reference];
        if (existing) {
            return existing.asObservable();
        }

        const subject = new ReplaySubject<ReferenceBookResponse[]>(1);
        this.pendingSubjects[reference] = subject;
        this.pendingTypes.add(reference);
        if (this.pendingTypes.size === 1) {
            queueMicrotask(() => this.flushPendingRequests());
        }
        return subject.asObservable();
    }

    preloadCommonDictionaries(): void {
        combineLatest(this.commonDictionaries.map(referenceBookName => this.getReference(referenceBookName)))
            .subscribe();
        this.getReference('bin')
            .subscribe(response => {
                this.authHolderService.setCurrentOrganization(response.find(item => item.code === this.authHolderService.organizationBin));
            });
    }

    addOrUpdateExistingCachedValue(value: ReferenceBookResponse): void {
        const existingDictionary = this.references[value.type.toLowerCase()];
        if (!existingDictionary?.length) {
            return;
        }
        const index = existingDictionary.findIndex((reference: ReferenceBookResponse) => reference.id === value.id);
        if (index === -1) {
            existingDictionary.push(value);
        } else if (!value.isDeleted) {
            existingDictionary[index] = value;
        } else {
            existingDictionary?.splice(index, 1);
        }
    }

    private flushPendingRequests(): void {
        const types = [...this.pendingTypes];
        this.pendingTypes.clear();

        this.referenceApi.getReferenceBookValues(types.map(type => type.toUpperCase() as ReferenceBookType)).subscribe({
            next: response => this.distributeBulkResponse(types, response),
            error: error => this.distributeBulkError(types, error),
        });
    }

    private distributeBulkResponse(types: string[], response: ReferenceBookResponse[]): void {
        types.forEach(type => {
            const items = response.filter(item => item.type?.toLowerCase() === type);
            this.references[type] = items;
            const subject = this.pendingSubjects[type];
            subject?.next(items);
            subject?.complete();
            delete this.pendingSubjects[type];
        });
    }

    private distributeBulkError(types: string[], error: unknown): void {
        types.forEach(type => {
            const subject = this.pendingSubjects[type];
            subject?.error(error);
            delete this.pendingSubjects[type];
        });
    }

    private loadBinTypes(): Observable<ReferenceBookResponse[]> {
        return this.getReference('bin').pipe(map(bins => {
            this.references.bintypes = Array
                .from(new Set(bins.map(item => item.code1))).map(item => <ReferenceBookResponse>{
                    code: item,
                    code1: item,
                    name_ru: this.localizationService.getLocalizedKey(`i18n_af_organizationType#${item}`),
                });
            return this.references.bintypes;
        }));
    }

    private loadCustomizedNotificationType(): Observable<ReferenceBookResponse[]> {
        return of(this.notificationTypes)
            .pipe(map(items => {
                this.references.notification_type = items;
                return [...new Set(items.map(item => item.code2))].map(groupName => <ReferenceBookResponse>{
                    code: groupName,
                    code1: groupName,
                    name_ru: this.localizationService.getLocalizedKey(`i18n_af_filter_field_notification_group_type#${groupName}`),
                });
            }));
    }

    private buildDictionaryFromEnum(enumDictionary: EnumDictionary, translationPrefix?: string): ReferenceBookResponse[] {
        return Object.values(enumDictionary)
            .map(value => <ReferenceBookResponse>{
                code: value,
                code1: value,
                name_ru: translationPrefix ? this.localizationService.getLocalizedKey(`${translationPrefix}${value}`) : value,
            });
    }

    private loadRoles(reference: string): Observable<ReferenceBookResponse[]> {
        if (!this.inProgressRolesRequest) {
            this.inProgressRolesRequest = this.roleApiService.getRolesDictionary().pipe(
                map(rolesResponse => {
                    this.references.roles = rolesResponse.simple.map(role => <ReferenceBookResponse>{
                        code: role,
                        code1: role,
                    });
                    this.references.composite_roles = rolesResponse.composite.map(role => <ReferenceBookResponse>{
                        code: role,
                        code1: role,
                    });
                    return this.references.roles;
                }),
                finalize(() => this.inProgressRolesRequest = null),
                shareReplay(1),
            );
        }
        return this.inProgressRolesRequest.pipe(map(() => this.references[reference]));
    }
}

export function findValueInDictionaryBy(
    dictionary: ReferenceBookResponse[],
    filterBy: string[],
    predefinedValue: string | string[] | null,
    isMultiple = false,
): string | string[] {
    let dictionaryValue = dictionary
        .filter(item => [item.code, item.code1, item.code2, item.name_ru].some(value => filterBy.includes(value)));

    if (predefinedValue !== null) {
        dictionaryValue = dictionaryValue.filter(item => [item.code, item.code1, item.code2, item.name_ru].some(value => {
                if (isArray(predefinedValue)) {
                    return predefinedValue.includes(value);
                }
                return predefinedValue === value;
            },
        ));
    }
    if (isMultiple) {
        return dictionaryValue?.map(item => item.code) ?? null;
    }
    return dictionaryValue.length > 0 ? dictionaryValue[0].code : null;
}

export function findValueInDictionary(
    dictionary: ReferenceBookResponse[],
    predefinedValue: string,
    returnValue: keyof ReferenceBookResponse = 'code',
): string {
    if (!predefinedValue) {
        return '-';
    }
    const dictionaryValue = dictionary.filter(item => [item.code, item.code1, item.code2, item.name_ru]
        .some(value => predefinedValue === value));
    let value: string;
    switch (returnValue) {
        case 'code1':
            value = dictionaryValue.length > 0 ? dictionaryValue[0].code1 : null;
            break;
        case 'code2':
            value = dictionaryValue.length > 0 ? dictionaryValue[0].code2 : null;
            break;
        case 'name_ru':
            value = dictionaryValue.length > 0 ? dictionaryValue[0].name_ru : null;
            break;
        default:
            value = dictionaryValue.length > 0 ? dictionaryValue[0].code : null;
            break;
    }
    return value;
}

declare type EnumDictionary = SubjectCategory | SubjectType | IncidentPriorityType;

export function resolveRefName(dictionary: ReferenceBookResponse[], code: string): string {
    if (!code) {
        return '';
    }
    const found = findValueInDictionary(dictionary, code, 'name_ru');
    return found && found !== '-' ? found : code;
}

export declare type ReferenceBookTypeExtended = ReferenceBookType | 'subject_type';
